// Client layer (TS-2.5.03) - UC-16..UC-20/UC-22, BRD section 8.6. Admin-gated at the route level
// (App.tsx: RequireRole roles=[Admin]) - every screen composed here assumes the caller is already
// an Admin, so this component owns only which tab is showing, never a second role check.
//
// The tab bar follows the WAI-ARIA tabs pattern (D-244): role=tablist/tab/tabpanel, roving
// tabindex (only the selected tab is in the Tab order), and automatic activation - ArrowLeft/
// ArrowRight/Home/End move focus AND select, wrapping at the ends. Because activation is
// automatic, focus always sits on the selected tab, so the keydown handler can derive the current
// index from state. One tabpanel element is reused across tabs (its aria-labelledby retargets);
// the panels stay conditionally MOUNTED, not hidden, so each panel's on-mount data reads keep
// firing exactly when they did before this markup existed.
import { useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { BrandingPanel } from "./BrandingPanel";
import { FieldMapPanel } from "./FieldMapPanel";
import { LayoutSettingsPanel } from "./LayoutSettingsPanel";
import { ProductsPanel } from "./ProductsPanel";
import { ReferenceDataPanel } from "./ReferenceDataPanel";
import { UsersAdminPanel } from "./UsersAdminPanel";
import { strings } from "../../strings";
import "./admin.css";

type AdminTab = "referenceData" | "layout" | "branding" | "fieldMaps" | "products" | "users";

const S = strings.admin;

const TABS: ReadonlyArray<readonly [AdminTab, string]> = [
  ["referenceData", S.tabs.referenceData],
  ["layout", S.tabs.layout],
  ["branding", S.tabs.branding],
  ["fieldMaps", S.tabs.fieldMaps],
  ["products", S.tabs.products],
  ["users", S.tabs.users],
];

export function AdminPage() {
  const [tab, setTab] = useState<AdminTab>("referenceData");
  const tabRefs = useRef<Partial<Record<AdminTab, HTMLButtonElement | null>>>({});

  function activate(next: AdminTab) {
    setTab(next);
    tabRefs.current[next]?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const index = TABS.findIndex(([key]) => key === tab);
    let next: number;
    switch (event.key) {
      case "ArrowRight":
        next = (index + 1) % TABS.length;
        break;
      case "ArrowLeft":
        next = (index - 1 + TABS.length) % TABS.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = TABS.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    activate(TABS[next]![0]);
  }

  return (
    <section className="eg-admin">
      <h1>{S.heading}</h1>

      <div role="tablist" className="eg-admin__tabs" aria-label={S.aria.tabsNav}>
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`eg-admin-tab-${key}`}
            aria-selected={tab === key}
            aria-controls="eg-admin-tabpanel"
            tabIndex={tab === key ? 0 : -1}
            ref={(element) => {
              tabRefs.current[key] = element;
            }}
            onClick={() => setTab(key)}
            onKeyDown={handleKeyDown}
          >
            {label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id="eg-admin-tabpanel"
        aria-labelledby={`eg-admin-tab-${tab}`}
        className="eg-admin__panel"
      >
        {tab === "referenceData" && <ReferenceDataPanel />}
        {tab === "layout" && <LayoutSettingsPanel />}
        {tab === "branding" && <BrandingPanel />}
        {tab === "fieldMaps" && <FieldMapPanel />}
        {tab === "users" && <UsersAdminPanel />}
        {tab === "products" && <ProductsPanel />}
      </div>
    </section>
  );
}
